import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [loyaltyModal, setLoyaltyModal] = useState(null); // { id, full_name }
  const [loyaltyForm, setLoyaltyForm] = useState({ delta: '', note: '' });
  const [adjusting, setAdjusting] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get('/api/admin/users', { params: { search } })
      .then((response) => setUsers(response.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [search]);

  const toggleBlock = async (id) => {
    try {
      const response = await axios.put(`/api/admin/users/${id}/block`);
      toast.success(`User ${response.data.status}`);
      load();
    } catch {
      toast.error('Failed to update user status');
    }
  };

  const createUser = async () => {
    setCreating(true);
    try {
      const response = await axios.post('/api/admin/users', form);
      toast.success(`Created. Shipping mark: ${response.data.shippingMark}`);
      setShowCreate(false);
      setForm({ full_name: '', email: '', phone: '', password: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const openLoyaltyModal = (user) => {
    setLoyaltyModal({ id: user.id, full_name: user.full_name });
    setLoyaltyForm({ delta: '', note: '' });
  };

  const adjustLoyalty = async () => {
    if (!loyaltyForm.delta) return toast.error('Enter a points amount');
    setAdjusting(true);
    try {
      await axios.put(`/api/admin/users/${loyaltyModal.id}/loyalty`, {
        delta: parseInt(loyaltyForm.delta, 10),
        note: loyaltyForm.note,
      });
      toast.success('Points adjusted');
      setLoyaltyModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to adjust points');
    } finally {
      setAdjusting(false);
    }
  };

  const startChat = (user) => {
    navigate('/admin/chats', {
      state: {
        startUser: {
          id: user.id,
          full_name: user.full_name,
          shipping_mark: user.shipping_mark,
          email: user.email,
        },
      },
    });
  };

  return (
    <div>
      {/* Loyalty Adjustment Modal */}
      {loyaltyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="card" style={{ width: '100%', maxWidth: 360 }}>
            <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Adjust Points</p>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>{loyaltyModal.full_name}</p>
            <div className="input-group">
              <label>Points (positive to add, negative to deduct)</label>
              <input
                className="input-field"
                type="number"
                placeholder="e.g. 100 or -50"
                value={loyaltyForm.delta}
                onChange={(e) => setLoyaltyForm({ ...loyaltyForm, delta: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ marginTop: 8 }}>
              <label>Reason / Note (optional)</label>
              <input
                className="input-field"
                type="text"
                placeholder="e.g. Compensation for delay"
                value={loyaltyForm.note}
                onChange={(e) => setLoyaltyForm({ ...loyaltyForm, note: e.target.value })}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="btn btn-primary btn-sm" onClick={adjustLoyalty} disabled={adjusting}>
                {adjusting ? <span className="spinner" /> : 'Confirm'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setLoyaltyModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <Link to="/admin" className="back-btn" aria-label="Back to dashboard">
          &lt;
        </Link>
        <h1>Users</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          New
        </button>
      </div>

      <div className="page-content">
        <input
          className="input-field"
          placeholder="Search by name, email, or mark"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ marginBottom: 12 }}
        />

        {showCreate && (
          <div className="card" style={{ border: '1px solid var(--gold)', marginBottom: 16 }}>
            <p style={{ fontWeight: 700, marginBottom: 12 }}>Create New Customer</p>

            {['full_name', 'email', 'phone', 'password'].map((key) => (
              <div className="input-group" key={key}>
                <label>{key.replace('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())}</label>
                <input
                  className="input-field"
                  type={key === 'password' ? 'password' : key === 'email' ? 'email' : 'text'}
                  value={form[key]}
                  onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                />
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={createUser} disabled={creating}>
                {creating ? <span className="spinner" /> : 'Create'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading-center">
            <div className="spinner spinner-dark" />
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="icon">Users</div>
            <p>No users found</p>
          </div>
        ) : (
          users.map((user) => (
            <div className="card" key={user.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 700, fontSize: 14 }}>{user.full_name}</p>
                  <p style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {user.email} · {user.phone}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--gold)', fontWeight: 700, marginTop: 2 }}>
                    {user.shipping_mark}
                  </p>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <span className={`badge badge-${user.loyalty_tier}`}>{user.loyalty_tier}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{user.loyalty_points} pts</span>
                    <span className={`badge ${user.status === 'active' ? 'badge-paid' : 'badge-unpaid'}`}>
                      {user.status}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 96 }}>
                  <button
                    onClick={() => startChat(user)}
                    className="btn btn-outline btn-sm"
                    type="button"
                  >
                    Chat
                  </button>
                  <button
                    onClick={() => openLoyaltyModal(user)}
                    className="btn btn-outline btn-sm"
                    type="button"
                  >
                    Points
                  </button>
                  <button
                    onClick={() => toggleBlock(user.id)}
                    className={`btn btn-sm ${user.status === 'active' ? 'btn-danger' : 'btn-success'}`}
                    type="button"
                  >
                    {user.status === 'active' ? 'Block' : 'Unblock'}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
